import React, { useEffect, useMemo, useState } from "react";
import api, { API, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Clock, Plus, CheckCircle2, X, Download, AlertTriangle, Send } from "lucide-react";

function mondayOf(dateStr) {
  const d = dateStr ? new Date(dateStr) : new Date();
  const day = (d.getDay() + 6) % 7; // Mon=0
  d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
}

const STATUS_BADGE = {
  Pending: { bg: "#fef3c7", fg: "#92400e" },
  Approved: { bg: "#dcfce7", fg: "#065f46" },
  Rejected: { bg: "#fee2e2", fg: "#b91c1c" },
};

export default function Timesheets() {
  const { user } = useAuth();
  const isOperative = user?.role === "operative";
  const canApprove = ["admin", "contracts_manager", "supervisor"].includes(user?.role);
  const canExport = ["admin", "contracts_manager"].includes(user?.role);

  const [weekStart, setWeekStart] = useState(mondayOf());
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    const params = { week_start: weekStart };
    if (statusFilter) params.status = statusFilter;
    const [r, s] = await Promise.all([
      api.get("/timesheets", { params }),
      api.get("/timesheets/summary").catch(() => ({ data: {} })),
    ]);
    setRows(r.data);
    setSummary(s.data || {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStart, statusFilter]);

  const approve = async (ts) => {
    setError(""); setSuccess("");
    try { await api.patch(`/timesheets/${ts.id}/approve`); setSuccess(`Approved ${ts.operative_name} · ${ts.date}`); load(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const reject = async (ts) => {
    const reason = window.prompt("Reason for rejection?");
    if (!reason) return;
    setError(""); setSuccess("");
    try { await api.patch(`/timesheets/${ts.id}/reject`, { reason }); setSuccess(`Rejected ${ts.operative_name} · ${ts.date}`); load(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const exportCsv = () => {
    const url = `${API}/timesheets/export.csv?week_start=${encodeURIComponent(weekStart)}`;
    window.open(url, "_blank");
    // refresh after a tick so the lock-flag flips
    setTimeout(load, 800);
  };

  const totalHours = useMemo(() => rows.reduce((acc, r) => acc + Number(r.hours || 0), 0), [rows]);

  return (
    <div data-testid="timesheets-page" style={{ padding: "24px 24px 64px" }}>
      <header style={{ marginBottom: 20, display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 12 }}>
        <div>
          <div className="mono" style={{ color: "#ff5a00", fontWeight: 600, fontSize: 12, letterSpacing: "0.1em" }}>PHASE 2A · HOURS → APPROVAL → XERO CSV</div>
          <h1 style={{ fontSize: 28, fontWeight: 900, margin: "4px 0 0" }}>Timesheets</h1>
          <div style={{ color: "#64748b", marginTop: 4 }}>Time attaches to a <strong>visit</strong>, not a structure. Operatives can only log against visits whose gang they&apos;re on.</div>
        </div>
        <button data-testid="new-timesheet-btn" className="btn-primary" onClick={() => setShowNew(true)}><Plus size={16} style={{ marginRight: 6, verticalAlign: "middle" }} /> Log time</button>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 16 }}>
        <Tile testid="tile-pending" label="Pending approvals" value={summary.pending ?? 0} accent="#ff5a00" />
        <Tile testid="tile-approved-week" label="Approved this week" value={summary.approved_this_week ?? 0} />
        <Tile testid="tile-hours-week" label="Hours approved (week)" value={(summary.hours_approved_this_week ?? 0).toFixed(1)} />
        <Tile testid="tile-week-hours" label="Hours shown" value={totalHours.toFixed(1)} />
      </div>

      {error ? <div data-testid="timesheets-error" className="card" style={{ padding: 12, marginBottom: 12, background: "#fef2f2", color: "#b91c1c", borderLeft: "4px solid #dc2626", display: "flex", gap: 8, alignItems: "center" }}><AlertTriangle size={16} /> {error}</div> : null}
      {success ? <div data-testid="timesheets-success" className="card" style={{ padding: 12, marginBottom: 12, background: "#ecfdf5", color: "#065f46", borderLeft: "4px solid #059669", display: "flex", gap: 8, alignItems: "center" }}><CheckCircle2 size={16} /> {success}</div> : null}

      <div className="card" style={{ padding: 12, marginBottom: 16, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
        <label className="mono" style={{ fontSize: 12, color: "#64748b" }}>
          WEEK STARTING
          <input data-testid="filter-week" type="date" value={weekStart} onChange={(e) => setWeekStart(mondayOf(e.target.value))} style={{ marginLeft: 8 }} />
        </label>
        <label className="mono" style={{ fontSize: 12, color: "#64748b" }}>
          STATUS
          <select data-testid="filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ marginLeft: 8 }}>
            <option value="">All</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>
        </label>
        {canExport ? (
          <button data-testid="export-csv-btn" className="btn-primary" style={{ marginLeft: "auto" }} onClick={exportCsv}>
            <Download size={14} style={{ marginRight: 6, verticalAlign: "middle" }} /> Export Xero CSV (locks approved rows)
          </button>
        ) : null}
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table className="responsive-table" data-testid="timesheets-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Operative</th>
              <th>Visit</th>
              <th>Structure</th>
              <th style={{ textAlign: "right" }}>Hours</th>
              <th>Status</th>
              <th>Notes</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 24, textAlign: "center", color: "#64748b" }}>No timesheets for this week.</td></tr>
            ) : rows.map((t) => {
              const badge = STATUS_BADGE[t.status] || { bg: "#f1f5f9", fg: "#334155" };
              return (
                <tr key={t.id} data-testid={`ts-row-${t.id}`}>
                  <td data-label="Date">{t.date}</td>
                  <td data-label="Operative"><strong>{t.operative_name}</strong></td>
                  <td data-label="Visit"><span className="mono" style={{ background: "#0f172a", color: "white", padding: "2px 8px", borderRadius: 3, fontSize: 11 }}>{t.visit_type || "—"}</span></td>
                  <td data-label="Structure" style={{ maxWidth: 260, color: "#64748b" }}>{t.structure_description || "—"}</td>
                  <td data-label="Hours" style={{ textAlign: "right", fontWeight: 700 }} data-testid={`ts-hours-${t.id}`}>{Number(t.hours).toFixed(1)}</td>
                  <td data-label="Status"><span data-testid={`ts-status-${t.id}`} className="mono" style={{ background: badge.bg, color: badge.fg, padding: "2px 8px", borderRadius: 3, fontSize: 11, fontWeight: 700 }}>{t.status}{t.locked ? " · LOCKED" : ""}</span></td>
                  <td data-label="Notes" style={{ fontSize: 12, color: "#64748b" }}>{t.notes || (t.rejected_reason ? `Rejected: ${t.rejected_reason}` : "")}</td>
                  <td data-label="Actions" style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    {canApprove && t.status === "Pending" && !t.locked ? (
                      <>
                        <button data-testid={`approve-${t.id}`} className="btn-primary" onClick={() => approve(t)}>Approve</button>
                        <button data-testid={`reject-${t.id}`} className="btn-ghost" style={{ marginLeft: 6 }} onClick={() => reject(t)}>Reject</button>
                      </>
                    ) : null}
                    {canApprove && t.status === "Rejected" && !t.locked ? (
                      <button data-testid={`approve-${t.id}`} className="btn-primary" onClick={() => approve(t)}>Approve</button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showNew ? (
        <NewTimesheetModal
          isOperative={isOperative}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); setSuccess("Timesheet submitted"); load(); }}
          onError={(msg) => setError(msg)}
        />
      ) : null}
    </div>
  );
}

function Tile({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="card" style={{ padding: 16, borderTop: `3px solid ${accent || "#0f172a"}` }}>
      <div className="mono" style={{ fontSize: 11, color: "#64748b", letterSpacing: "0.1em", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 900, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function NewTimesheetModal({ isOperative, onClose, onCreated, onError }) {
  const [visits, setVisits] = useState([]);
  const [operatives, setOperatives] = useState([]);
  const [form, setForm] = useState({ operative_id: "", visit_id: "", date: new Date().toISOString().slice(0, 10), hours: "", notes: "" });
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/visits", { params: { status: "InProgress" } }).then((r) => setVisits(r.data)).catch(() => {});
    api.get("/visits", { params: { status: "Planned" } }).then((r) => setVisits((prev) => [...prev, ...r.data])).catch(() => {});
    if (!isOperative) {
      api.get("/operatives").then((r) => setOperatives(r.data.filter((o) => o.status === "active")));
    } else {
      // operative submits for self — preselect own operative record by fetching visit gang membership
      api.get("/operatives").then((r) => {
        const all = r.data;
        setOperatives(all);
        // We don't know which op is "me" — backend enforces. We'll try the first gang membership match below.
      });
    }
  }, [isOperative]);

  // For operatives, narrow visits to ones where the user-as-op is on the gang.
  // We auto-pick the operative_id by intersecting gangs.
  useEffect(() => {
    if (!isOperative || operatives.length === 0 || visits.length === 0) return;
    // For each gang membership in visits, collect candidate operative_ids;
    // pick the one that appears across the user's visible visits (backend already scoped operative_id → me).
    const ids = new Set();
    visits.forEach((v) => (v.gang || []).forEach((g) => ids.add(g.operative_id)));
    if (ids.size > 0 && !form.operative_id) {
      setForm((f) => ({ ...f, operative_id: Array.from(ids)[0] }));
      setMe(Array.from(ids)[0]);
    }
  }, [isOperative, operatives, visits, form.operative_id]);

  const visitOptions = isOperative
    ? visits.filter((v) => (v.gang || []).some((g) => g.operative_id === (me || form.operative_id)))
    : visits;

  const submit = async () => {
    if (!form.visit_id) { onError("Pick a visit"); return; }
    if (!form.operative_id) { onError("Pick the operative"); return; }
    if (!form.hours || Number(form.hours) <= 0) { onError("Hours required"); return; }
    setBusy(true);
    try {
      await api.post("/timesheets", {
        operative_id: form.operative_id,
        visit_id: form.visit_id,
        date: form.date,
        hours: Number(form.hours),
        notes: form.notes,
      });
      onCreated();
    } catch (e) {
      onError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 80 }}>
      <div data-testid="new-timesheet-modal" className="card" style={{ background: "white", maxWidth: 560, width: "100%", padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontWeight: 900 }}>Log time</h2>
          <button data-testid="close-new-ts" className="btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>

        {!isOperative ? (
          <Field label="Operative *">
            <select data-testid="ts-operative" value={form.operative_id} onChange={(e) => setForm({ ...form, operative_id: e.target.value, visit_id: "" })}>
              <option value="">— pick operative —</option>
              {operatives.map((o) => <option key={o.id} value={o.id}>{o.full_name} · {o.role}</option>)}
            </select>
          </Field>
        ) : (
          <div className="mono" style={{ fontSize: 11, color: "#64748b", padding: "4px 0" }}>Logging time as: <strong style={{ color: "#0f172a" }}>{(operatives.find((o) => o.id === (me || form.operative_id)) || {}).full_name || "you"}</strong></div>
        )}

        <Field label="Visit *">
          <select data-testid="ts-visit" value={form.visit_id} onChange={(e) => setForm({ ...form, visit_id: e.target.value })}>
            <option value="">— pick a visit —</option>
            {visitOptions.map((v) => (
              <option key={v.id} value={v.id}>
                {v.type} · {v.structure_description?.slice(0, 60)} · gang {(v.gang || []).length}
              </option>
            ))}
          </select>
          {visitOptions.length === 0 ? (
            <div className="mono" style={{ fontSize: 11, color: "#dc2626", marginTop: 4 }}>No eligible visits — must be on the visit&apos;s gang.</div>
          ) : null}
        </Field>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Date *">
            <input data-testid="ts-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </Field>
          <Field label="Hours *">
            <input data-testid="ts-hours" type="number" min="0.25" max="24" step="0.25" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} />
          </Field>
        </div>

        <Field label="Notes">
          <textarea data-testid="ts-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </Field>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button data-testid="submit-new-ts" className="btn-primary" onClick={submit} disabled={busy}><Send size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />{busy ? "Submitting…" : "Submit"}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block", marginTop: 8 }}>
      <div className="mono" style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>{label}</div>
      {children}
    </label>
  );
}
